import {computed, ref} from "vue";
import {useLog} from "@/composables/useLog";
import {authFetch} from "@/composables/useAuthFetch";

const log = useLog("useProfileContent");

const bioText = ref("");
const testimonialText = ref("");
const savedBioText = ref("");
const savedTestimonialText = ref("");
const isLoaded = ref(false);
const isLoading = ref(false);
const savingTab = ref(null);
const saveStatus = ref(null);
const SAVED_MESSAGE_MS = 5000; // "Saved" shows briefly, then clears
let savedTimer = null;

function showSaved() {
  saveStatus.value = "success";
  clearTimeout(savedTimer);
  savedTimer = setTimeout(() => {
    if (saveStatus.value === "success") saveStatus.value = null;
  }, SAVED_MESSAGE_MS);
}

const isSaving = computed(() => savingTab.value !== null);
const isBioFocused = ref(false);
const isBioEditing = ref(false); // the bio block is open for editing (toolbar shown)
const isTestimonialEditing = ref(false);
const isTestimonialFocused = ref(false);
const isBioDirty = computed(() => bioText.value !== savedBioText.value);
const isTestimonialDirty = computed(() => testimonialText.value !== savedTestimonialText.value);
// rendered-text lengths, reported by the editors (TipTap doc.textContent), not HTML
const bioTextLength = ref(0);
const testimonialTextLength = ref(0);
const isBioOverLimit = computed(() => bioTextLength.value > 800);
const isTestimonialOverLimit = computed(() => testimonialTextLength.value > 800);

async function loadContent() {
  if (isLoaded.value || isSaving.value) return;
  isLoading.value = true;
  try {
    const response = await authFetch("/api/v1/user/profile");
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    if (!isSaving.value) {
      bioText.value = data.bio ?? "";
      testimonialText.value = data.testimonial ?? "";
      savedBioText.value = bioText.value;
      savedTestimonialText.value = testimonialText.value;
    }
    isLoaded.value = true;
  } catch (error) {
    log(`loadContent error: ${error?.message}`);
  } finally {
    isLoading.value = false;
  }
}

async function saveContent(tab) {
  if (!isBioDirty.value && !isTestimonialDirty.value) return;
  savingTab.value = tab;
  saveStatus.value = null;
  try {
    const body = {bio: bioText.value, testimonial: testimonialText.value};
    const response = await authFetch("/api/v1/user/profile", {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    bioText.value = data.bio ?? bioText.value;
    testimonialText.value = data.testimonial ?? testimonialText.value;
    savedBioText.value = bioText.value;
    savedTestimonialText.value = testimonialText.value;
    isLoaded.value = true;
    showSaved();
  } catch (error) {
    log(`saveContent error: ${error?.message}`);
    saveStatus.value = "error";
  } finally {
    savingTab.value = null;
  }
}

function startBioEditing() {
  isBioEditing.value = true;
}

function endBioEditing() {
  isBioEditing.value = false;
}

// Escape or closing the dialog: unsaved bio changes are thrown away
function discardBioEdits() {
  bioText.value = savedBioText.value;
  isBioEditing.value = false;
}

function startTestimonialEditing() {
  isTestimonialEditing.value = true;
}

function endTestimonialEditing() {
  isTestimonialEditing.value = false;
}

function discardTestimonialEdits() {
  testimonialText.value = savedTestimonialText.value;
  isTestimonialEditing.value = false;
}

export function useProfileContent() {
  return {
    bioText, testimonialText, isLoading, isSaving, savingTab, saveStatus,
    isBioFocused, isTestimonialFocused, isBioDirty, isTestimonialDirty,
    isBioOverLimit, isTestimonialOverLimit, bioTextLength, testimonialTextLength,
    isBioEditing, startBioEditing, endBioEditing, discardBioEdits,
    isTestimonialEditing, startTestimonialEditing, endTestimonialEditing, discardTestimonialEdits,
    loadContent, saveContent,
  };
}
